import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, ShieldCheck, KeyRound, Banknote, Sparkles,
  CheckCircle2, Loader2, Phone, Building2, User as UserIcon,
  Activity,
} from "lucide-react";
import BankDemoShell from "./BankDemoShell";
import AuditPanel from "./AuditPanel";
import { newTrail, addEvent } from "./auditTrail";

// /bank-demo/buyer — sandboxed 6-step buyer signup walkthrough.
// Pre-filled with demo data; mocked compliance outcomes; no API calls.

const PREFILL = {
  first_name: "Alex",
  last_name: "Chen",
  email: "alex@apexgoods-demo.com",
  phone: "+1 (415) 555-0142",
  legal_name: "Apex Goods LLC",
  ein: "84-2156033",
  business_type: "LLC",
  address: "1287 Mission St, Suite 410",
  city: "San Francisco",
  state: "CA",
  postal_code: "94103",
  owner_name: "Alex Chen",
  owner_dob: "1989-04-12",
  owner_ssn_last4: "0143",
  categories: ["Beauty & Personal Care", "Home & Kitchen"],
  gmv_band: "$1M – $10M",
};

const STEPS = [
  { id: 1, label: "Account",         icon: UserIcon },
  { id: 2, label: "Business · KYB",  icon: Building2 },
  { id: 3, label: "Owner · KYC",     icon: KeyRound },
  { id: 4, label: "Bank · Plaid",    icon: Banknote },
  { id: 5, label: "AI underwriter",  icon: Sparkles },
  { id: 6, label: "Approved",        icon: CheckCircle2 },
];

export default function BankDemoBuyerFlow() {
  const [step, setStep] = useState(1);
  const [trail, setTrail] = useState(newTrail());
  // Per-step "running" flag for the mocked async control (Twilio, Middesk, Plaid, Claude).
  const [running, setRunning] = useState(false);
  // Per-step "completed" flag — once a control has been simulated, advancing reuses it.
  const [done, setDone] = useState({}); // { 1: true, 2: true ... }

  const pct = useMemo(() => Math.round(((step - 1) / (STEPS.length - 1)) * 100), [step]);

  // Simulate an async compliance control firing.
  // Each step has a list of events; we add them to the audit trail with a small
  // staggered delay so the panel reads as a live timeline.
  const fireControl = async (events, finalize) => {
    setRunning(true);
    for (const ev of events) {
      // start as pending, then resolve to success
      const pending = { ...ev, status: "pending" };
      setTrail((t) => addEvent(t, pending));
      // eslint-disable-next-line no-await-in-loop
      await sleep(700 + Math.random() * 350);
      setTrail((t) =>
        // replace the most recent (pending) instance of this action with the final
        replacePending(t, ev.action, { ...ev, status: ev.finalStatus || "success" }),
      );
    }
    setDone((d) => ({ ...d, [step]: true }));
    if (finalize) finalize();
    setRunning(false);
  };

  const goto = (n) => {
    if (n >= 1 && n <= STEPS.length) setStep(n);
  };

  return (
    <BankDemoShell
      crumbs={[
        { label: "Bank Demo", to: "/bank-demo" },
        { label: "Buyer flow" },
      ]}
    >
      {/* Stepper ───────────────────────────────────────────────── */}
      <div className="mb-8" data-testid="bank-demo-buyer-stepper">
        <div className="flex flex-wrap items-center gap-3">
          {STEPS.map((s) => {
            const active = s.id === step;
            const past = s.id < step;
            return (
              <button
                key={s.id}
                onClick={() => goto(s.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors ${
                  active
                    ? "bg-white text-[#0b0d12] border-white"
                    : past
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
                    : "bg-white/5 text-white/55 border-white/15 hover:text-white"
                }`}
                data-testid={`bank-demo-buyer-step-${s.id}`}
              >
                <s.icon className="w-3.5 h-3.5" />
                {s.id}. {s.label}
              </button>
            );
          })}
        </div>
        <div className="mt-4 h-1 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-emerald-400 transition-all duration-500"
            style={{ width: `${pct}%` }}
            data-testid="bank-demo-buyer-progress"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        {/* MAIN STEP CONTENT ───────────────────────────────────── */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
          {step === 1 && (
            <Step1Account
              running={running}
              done={!!done[1]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "Buyer · Alex Chen",
                      action: "auth.signup.attempted",
                      resource: "user:demo",
                      meta: "Email + phone captured · password ≥ 8 chars",
                      finalStatus: "success",
                    },
                    {
                      actor: "Twilio Verify",
                      action: "sms.otp.verified",
                      resource: `phone:${PREFILL.phone}`,
                      meta: "OTP sent to +1 (415) ●●●-0142 · trust cookie issued (30d)",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(2),
                )
              }
            />
          )}
          {step === 2 && (
            <Step2KYB
              running={running}
              done={!!done[2]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "Middesk",
                      action: "kyb.business.verified",
                      resource: `ein:${PREFILL.ein}`,
                      meta: `EIN matches "Apex Goods LLC" · CA Sec. of State: ACTIVE · officers match`,
                      finalStatus: "success",
                    },
                    {
                      actor: "Compliance · OFAC scan",
                      action: "ofac.business.scanned",
                      resource: `ein:${PREFILL.ein}`,
                      meta: "0 hits SDN · 0 hits Sectoral · cleared 2026-02-08",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(3),
                )
              }
            />
          )}
          {step === 3 && (
            <Step3KYC
              running={running}
              done={!!done[3]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "Compliance · KYC",
                      action: "kyc.owner.verified",
                      resource: "owner:alex_chen",
                      meta: `DOB 1989-04-12 (age 36 · 18+ ✓) · last-4 SSN 0143 · ID match`,
                      finalStatus: "success",
                    },
                    {
                      actor: "Compliance · OFAC scan",
                      action: "ofac.individual.scanned",
                      resource: "owner:alex_chen",
                      meta: "0 hits SDN · 0 PEP · 0 adverse media (D&B)",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(4),
                )
              }
            />
          )}
          {step === 4 && (
            <Step4Plaid
              running={running}
              done={!!done[4]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "Plaid · Link",
                      action: "plaid.item.linked",
                      resource: "plaid_item:item_4f2a",
                      meta: "Chase · 4 accounts · 62 days transaction history · ACH-eligible",
                      finalStatus: "success",
                    },
                    {
                      actor: "Plaid · Auth",
                      action: "plaid.account.ownership_verified",
                      resource: "plaid_item:item_4f2a",
                      meta: "Account holder name matches signup name · ownership ✓",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(5),
                )
              }
            />
          )}
          {step === 5 && (
            <Step5AI
              running={running}
              done={!!done[5]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "AI · Verqo AI",
                      action: "credit.decision.issued",
                      resource: "credit_line:cl_9d2e",
                      meta: "Approved · Net-30 · $25,000 · risk band B · rationale stored",
                      finalStatus: "success",
                    },
                    {
                      actor: "Risk engine · TX monitoring",
                      action: "monitoring.rules.activated",
                      resource: "buyer:apex_demo",
                      meta: "Velocity, structuring, geography, BIN-mismatch rules: ON",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(6),
                )
              }
            />
          )}
          {step === 6 && <Step6Approved onRestart={() => { setTrail(newTrail()); setDone({}); setStep(1); }} />}

          {/* Footer nav (Back) ───────────────────────────────── */}
          <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
            <button
              type="button"
              disabled={step === 1 || running}
              onClick={() => goto(step - 1)}
              className="inline-flex items-center gap-1.5 text-[13px] text-white/65 hover:text-white disabled:text-white/30 disabled:cursor-not-allowed"
              data-testid="bank-demo-buyer-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <Link
              to="/bank-demo"
              className="text-[12.5px] text-white/55 hover:text-white"
              data-testid="bank-demo-buyer-exit"
            >
              Exit demo
            </Link>
          </div>
        </section>

        {/* AUDIT PANEL ─────────────────────────────────────────── */}
        <AuditPanel trail={trail} />
      </div>
    </BankDemoShell>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const replacePending = (trail, action, replacement) => {
  // Walk back through trail; replace the LAST pending event matching this action.
  const next = [...trail];
  for (let i = next.length - 1; i >= 0; i -= 1) {
    if (next[i].action === action && next[i].status === "pending") {
      next[i] = { ...next[i], ...replacement };
      break;
    }
  }
  return next;
};

// ─── Step blocks ─────────────────────────────────────────────────────────
function StepHeader({ step, total, title, sub, control }) {
  return (
    <div className="border-b border-white/10 pb-5 mb-6">
      <div className="text-[11.5px] uppercase tracking-wide text-white/55">
        Step {step} of {total} · {control}
      </div>
      <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-[14px] text-white/60 leading-relaxed">{sub}</p>
    </div>
  );
}

function PrefillField({ label, value, hint }) {
  return (
    <div>
      <div className="text-[11.5px] uppercase tracking-wide text-white/50 mb-1">{label}</div>
      <div className="rounded-lg border border-white/10 bg-white/5 px-3.5 py-2.5 text-[13.5px] font-medium text-white/90">
        {value}
      </div>
      {hint && <div className="text-[11.5px] text-white/45 mt-1.5">{hint}</div>}
    </div>
  );
}

function ContinueBtn({ onClick, running, done, label, doneLabel = "Continue", testid }) {
  return (
    <button
      onClick={onClick}
      disabled={running}
      className="inline-flex items-center gap-2 rounded-full bg-white text-[#0b0d12] px-5 py-3 text-[14px] font-semibold hover:bg-white/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid={testid}
    >
      {running ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" /> Running compliance check…
        </>
      ) : done ? (
        <>
          {doneLabel} <ArrowRight className="w-4 h-4" />
        </>
      ) : (
        <>
          {label} <ArrowRight className="w-4 h-4" />
        </>
      )}
    </button>
  );
}

function Step1Account({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={1}
        total={6}
        control="Twilio SMS step-up"
        title="Create the account"
        sub="The buyer enters their personal details. We send a one-time SMS code via Twilio Verify and issue a 30-day device-trust cookie. No password is reused — bcrypt hashed at rest."
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <PrefillField label="First name"  value={PREFILL.first_name} />
        <PrefillField label="Last name"   value={PREFILL.last_name} />
        <PrefillField label="Email"       value={PREFILL.email}     hint="MX-validated · disposable-domain blocklist applied" />
        <PrefillField label="Phone"       value={PREFILL.phone}     hint="Twilio carrier-type lookup · VOIP rejected" />
      </div>
      <div className="mt-6 rounded-xl border border-sky-500/20 bg-sky-500/10 p-4 text-[13px] text-sky-100">
        <div className="flex items-center gap-2 font-medium">
          <Phone className="w-4 h-4" />
          What happens next
        </div>
        <p className="mt-1.5 text-sky-100/85 leading-relaxed">
          Clicking <span className="font-mono">Continue</span> simulates the Twilio Verify SMS round-trip and audits a
          successful OTP match. Real production fires a 6-digit code to the phone above.
        </p>
      </div>
      <div className="mt-6">
        <ContinueBtn
          onClick={onContinue}
          running={running}
          done={done}
          label="Send OTP & continue"
          testid="bank-demo-buyer-step1-continue"
        />
      </div>
    </>
  );
}

function Step2KYB({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={2}
        total={6}
        control="Middesk KYB · OFAC SDN scan"
        title="Verify the business"
        sub="We pull the secretary-of-state record, EIN match, registered agent, officers, and dissolution status from Middesk. The legal entity is screened against OFAC SDN + Sectoral lists in the same call."
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <PrefillField label="Legal name"     value={PREFILL.legal_name} />
        <PrefillField label="EIN"             value={PREFILL.ein}            hint="Format-validated 9-digit federal tax ID" />
        <PrefillField label="Business type"   value={PREFILL.business_type} />
        <PrefillField label="Country"         value="United States" />
        <div className="sm:col-span-2">
          <PrefillField label="Registered address" value={`${PREFILL.address}, ${PREFILL.city}, ${PREFILL.state} ${PREFILL.postal_code}`} />
        </div>
      </div>
      <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-[13px] text-emerald-100">
        <div className="flex items-center gap-2 font-medium">
          <ShieldCheck className="w-4 h-4" />
          KYB controls fired on continue
        </div>
        <ul className="mt-2 space-y-1 list-disc list-inside text-emerald-100/85">
          <li>Middesk: business name + EIN match · SoS active · officers present</li>
          <li>OFAC: SDN, Sectoral Sanctions, Non-SDN Lists · 0 expected hits</li>
          <li>Address verified via USPS CASS · physical occupancy confirmed</li>
        </ul>
      </div>
      <div className="mt-6">
        <ContinueBtn
          onClick={onContinue}
          running={running}
          done={done}
          label="Run KYB & continue"
          testid="bank-demo-buyer-step2-continue"
        />
      </div>
    </>
  );
}

function Step3KYC({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={3}
        total={6}
        control="KYC · 18+ DOB validation · OFAC individual scan"
        title="Verify the beneficial owner"
        sub="Per BSA/AML, every account requires a beneficial owner and control person. We capture full legal name, DOB, last-4 SSN, and screen the individual against OFAC + PEP + adverse media lists."
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <PrefillField label="Owner legal name" value={PREFILL.owner_name} />
        <PrefillField label="Date of birth"    value={PREFILL.owner_dob} hint="Validated 18+ · max 120y" />
        <PrefillField label="Last 4 of SSN"    value={`●●●-●●-${PREFILL.owner_ssn_last4}`} hint="Stored encrypted at rest · only last 4 retained" />
        <PrefillField label="Owner phone"      value={PREFILL.phone} />
      </div>
      <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-[13px] text-emerald-100">
        <div className="flex items-center gap-2 font-medium">
          <KeyRound className="w-4 h-4" />
          KYC controls fired on continue
        </div>
        <ul className="mt-2 space-y-1 list-disc list-inside text-emerald-100/85">
          <li>OFAC: SDN + PEP + adverse media (D&B) · 0 expected hits</li>
          <li>18+ check: today − DOB ≥ 18y · ≤ 120y</li>
          <li>Identity match: name + DOB + last-4 SSN against credit bureau</li>
        </ul>
      </div>
      <div className="mt-6">
        <ContinueBtn
          onClick={onContinue}
          running={running}
          done={done}
          label="Run KYC & continue"
          testid="bank-demo-buyer-step3-continue"
        />
      </div>
    </>
  );
}

function Step4Plaid({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={4}
        total={6}
        control="Plaid Link · Account ownership · Cashflow signals"
        title="Link the bank account"
        sub="Buyer connects their primary operating account via Plaid Link. We use the Auth product (account ownership) + Transactions (60-day cashflow) to enrich the underwriting score. No credentials touch our servers."
      />
      <div className="rounded-xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#005194] grid place-items-center text-white font-bold">P</div>
          <div>
            <div className="text-[14px] font-semibold">Plaid Link · sandbox</div>
            <div className="text-[12px] text-white/55">Institution: Chase · 4 accounts</div>
          </div>
        </div>
        <ul className="mt-4 divide-y divide-white/5">
          {[
            { name: "Operating",   acct: "•••• 4421", bal: "$182,447.12" },
            { name: "Sweep",       acct: "•••• 9988", bal: "$28,109.40" },
            { name: "Tax escrow",  acct: "•••• 0042", bal: "$45,200.00" },
            { name: "Card autopay",acct: "•••• 7710", bal: "$8,940.55" },
          ].map((a) => (
            <li key={a.acct} className="py-2.5 flex items-center justify-between text-[13px]">
              <div>
                <div className="font-medium">{a.name}</div>
                <div className="text-white/55 text-[12px]">{a.acct}</div>
              </div>
              <div className="font-mono">{a.bal}</div>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-[13px] text-emerald-100">
        <div className="flex items-center gap-2 font-medium">
          <Banknote className="w-4 h-4" />
          Plaid signals fed into underwriting
        </div>
        <ul className="mt-2 space-y-1 list-disc list-inside text-emerald-100/85">
          <li>Account ownership: holder name matches signup name</li>
          <li>62 days of transaction history · 4.1% NSF rate (well below 8% threshold)</li>
          <li>Inflow trend: +14% MoM · payout-to-supplier velocity stable</li>
        </ul>
      </div>
      <div className="mt-6">
        <ContinueBtn
          onClick={onContinue}
          running={running}
          done={done}
          label="Confirm Plaid link & continue"
          testid="bank-demo-buyer-step4-continue"
        />
      </div>
    </>
  );
}

function Step5AI({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={5}
        total={6}
        control="AI Underwriter · Verqo AI"
        title="Underwriting & limit issuance"
        sub="Verqo AI takes the KYB, KYC, Plaid signals, and category mix as inputs and returns a credit decision with a structured rationale. The decision is logged immutably with a model version, input hash, and explainability id."
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <PrefillField label="Categories" value={PREFILL.categories.join(" · ")} />
        <PrefillField label="Annual GMV band" value={PREFILL.gmv_band} />
      </div>
      <div className="mt-6 rounded-xl border border-white/10 bg-[#070910] p-5">
        <div className="text-[11.5px] uppercase tracking-wide text-white/55">Decision preview · what Verqo AI will return</div>
        <table className="mt-4 w-full text-[13px]">
          <tbody className="divide-y divide-white/5">
            <tr><td className="py-2.5 text-white/65">Decision</td><td className="py-2.5 text-right text-emerald-300 font-semibold">Approved</td></tr>
            <tr><td className="py-2.5 text-white/65">Limit</td><td className="py-2.5 text-right font-mono">$25,000</td></tr>
            <tr><td className="py-2.5 text-white/65">Term</td><td className="py-2.5 text-right">Net-30</td></tr>
            <tr><td className="py-2.5 text-white/65">Risk band</td><td className="py-2.5 text-right">B (medium-low)</td></tr>
            <tr><td className="py-2.5 text-white/65">Pricing</td><td className="py-2.5 text-right">2.4% flat fee per invoice</td></tr>
            <tr><td className="py-2.5 text-white/65">Re-eval cadence</td><td className="py-2.5 text-right">60 days (post on-time history)</td></tr>
          </tbody>
        </table>
      </div>
      <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-[12.5px] text-white/65 leading-relaxed">
        <span className="text-white/85 font-medium">Rationale (excerpt):</span>{" "}
        Strong KYB & KYC outcomes; OFAC clean for entity & owner; Plaid shows 62d history with stable inflows; sector
        (Beauty/Home) historical default rate 0.38%. Recommend Net-30 at $25K. Re-evaluate for Net-60 after 60d
        on-time payment history.
      </div>
      <div className="mt-6">
        <ContinueBtn
          onClick={onContinue}
          running={running}
          done={done}
          label="Run AI underwriter & continue"
          testid="bank-demo-buyer-step5-continue"
        />
      </div>
    </>
  );
}

function Step6Approved({ onRestart }) {
  return (
    <>
      <StepHeader
        step={6}
        total={6}
        control="Account live · monitoring active"
        title="Approved · welcome to the platform"
        sub="The buyer's credit line is live. TX monitoring rules armed. The bank receives a daily JSONL bundle of every audit event from this account."
      />
      <div className="grid sm:grid-cols-3 gap-4">
        <Tile k="Credit line" v="$25,000" sub="Net-30 · band B" tone="emerald" />
        <Tile k="Decision SLA" v="94 sec" sub="Soft pull · explainable rationale" tone="white" />
        <Tile k="Daily settlement" v="D+1" sub="ACH next-day to seller" tone="white" />
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-[12px] uppercase tracking-wide text-white/55 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Active controls
          </div>
          <ul className="mt-3 space-y-1.5 text-[13px] text-white/85">
            <li>· Step-up SMS on bank/limit changes</li>
            <li>· Velocity rule: ≤ 5 invoices/24h to new sellers</li>
            <li>· Structuring rule: ≥ 3 invoices ≤ $10K within 72h</li>
            <li>· BIN-country mismatch flag</li>
            <li>· Re-screen OFAC every 24h (incremental)</li>
          </ul>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-[12px] uppercase tracking-wide text-white/55 flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" /> Sponsor bank deliverables
          </div>
          <ul className="mt-3 space-y-1.5 text-[13px] text-white/85">
            <li>· Daily audit JSONL → bank S3 (signed Ed25519)</li>
            <li>· Monthly attestation report (hash chain)</li>
            <li>· Quarterly SAR-eligible bundle</li>
            <li>· Real-time webhook on AML hits ≥ severity HIGH</li>
            <li>· Decision rationale & input hash on every credit issuance</li>
          </ul>
        </div>
      </div>
      <div className="mt-7 flex flex-wrap gap-3">
        <button
          onClick={onRestart}
          className="inline-flex items-center gap-2 rounded-full bg-white text-[#0b0d12] px-5 py-2.5 text-[13.5px] font-semibold hover:bg-white/90"
          data-testid="bank-demo-buyer-restart"
        >
          Restart the flow
        </button>
        <Link
          to="/bank-demo/seller"
          className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-[13.5px] font-medium hover:bg-white/5"
          data-testid="bank-demo-buyer-go-seller"
        >
          See the seller flow <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/bank-demo"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-[13.5px] font-medium text-white/65 hover:text-white"
          data-testid="bank-demo-buyer-back-to-landing"
        >
          Back to overview
        </Link>
      </div>
    </>
  );
}

function Tile({ k, v, sub, tone }) {
  const cls = tone === "emerald"
    ? "border-emerald-500/25 bg-emerald-500/10"
    : "border-white/10 bg-white/[0.03]";
  return (
    <div className={`rounded-xl border ${cls} p-5`}>
      <div className="text-[11.5px] uppercase tracking-wide text-white/55">{k}</div>
      <div className={`mt-2 text-2xl font-semibold tracking-tight ${tone === "emerald" ? "text-emerald-300" : ""}`}>{v}</div>
      <div className="text-[12px] text-white/55 mt-0.5">{sub}</div>
    </div>
  );
}
